/**
 * World 7 — "Ash Fields" (Fire). Narrative fragment for campaignStories.ts.
 * ACT II — THE FIRST KEEPER. Dialogue voiced at Pokémon Red trainer complexity.
 *
 * THE TALE (Act II, chapter 2 — "The Unremembered"): the First Keeper fled past
 * the island's edge, and his grief relit every fire he passed. Here burns the
 * Hurt Spirit — a soul CLAN Z caged and broke years back, neither freed nor ended.
 * Zeck the Boss finally shows himself on the smoke trail, Storm the Voltaryx near
 * full grown. Cliff's second erosion beat lands mid-watch. Old Man Gray tempts the
 * player at the embers and is refused. Chirpy reaches stage 3 shielding the embers.
 * THE PLAYER kneels, names the Hurt Spirit, and stays. Zeck watches a Keeper undo his
 * whole life of caging, his first crack of conscience. Soot, the Ash Guardian (Hotpaw,
 * flickering), grants the first shard of the FIRST SOUL alongside its own Soul
 * Shard. Count: 14.
 */

import type { NodeStory } from '../../campaignStories.js';

export const WORLD7_TAGLINE =
  "Gray's trail leads through fields that never stop burning. Clan Z left something hurt in the Ash Temple. Their boss has come back for it.";

export const WORLD7_ZONE_STORIES: Record<string, string> = {
  z18: "The map ends here. Gray's footprints keep going. So do the wheel marks from Clan Z's cages.",
  z19: "The fields burn without wood. Gray dust covers the trail markers. Cliff wipes each one clean to read the arrow.",
  z20: "A fire still burns inside the broken cage. Go slowly. The spirit inside remembers what Clan Z did.",
};

export const WORLD7_NODE_STORIES: Record<string, NodeStory> = {
  // --- Zone 18: Smoke Trail (1v1) ---
  "z18-n1": {
    story: "A big man with a glowing cage steps out of the smoke toward you. Behind him, a stone arch lies split in two.",
    dialogue: {
      intro: "I'm Zeck, boss of Clan Z. Storm split that arch with a yawn. My cages keep them safe. Don't wake him.",
      playerIntro: "Cages are not safe. Let them go.",
      playerWin: "Storm, fall back! The cage... popped open. My Voltaryx shard flew home to you. That makes thirteen for you. I need all nineteen!",
      playerLose: "See? Storm guards my cages. Nothing leaves.",
      introRematchAfterLoss: "Back for Storm? He hasn't forgotten you.",
      introRematchAfterWin: "Storm, this time we hold the trail. No backing down.",
      playerWinRepeat: "Go, then. I still have my keys.",
      playerLoseRepeat: "My cages hold. Nothing gets out.",
    },
  },
  "z18-nw1": {
    story: "A wild fire beast leaps at you from the hot ash.",
    dialogue: {
      intro: "A wild Zaricat leaps from the ash! It bars the trail!",
      playerWin: "The wild beast bolts up the smoking trail.",
      playerLose: "The wild beast drives you back down.",
      introRematchAfterLoss: "The trail heats up. The wild beast returns.",
      introRematchAfterWin: "The beast knows your scent. It bares its claws.",
      playerWinRepeat: "The wild beast flees into the heat.",
      playerLoseRepeat: "The trail stays wild and hot.",
    },
  },
  "z18-n2": {
    story: "Behind you, Chirpy won't budge from some stray embers. Timmy can't get her to move.",
    dialogue: {
      intro: "Wait up! Chirpy won't leave those embers. The next shard isn't going anywhere, you know!",
      playerWin: "I lost! But look at Chirpy glow! I think she's growing!",
      playerLose: "Ha! I win! Even Chirpy is laughing!",
      introRematchAfterLoss: "Back? Chirpy has been practicing her hero poses!",
      introRematchAfterWin: "You again! Chirpy's been glowing ever since!",
      playerWinRepeat: "Lost again! That's like ten times, right?",
      playerLoseRepeat: "I won again! Chirpy did a happy hop!",
    },
  },
  "z18-n3": {
    story: "You find Lulu walking alone, drawing her own map.",
    dialogue: {
      intro: "Mimi's at camp. I wanted to scout on my own. Practice round?",
      playerWin: "...You win.",
      playerLose: "I won. I'll tell her later. She'll say it was her idea.",
      introRematchAfterLoss: "Back. Still just me out here.",
      introRematchAfterWin: "You again. Fine.",
      playerWinRepeat: "You win again. I will tell Mimi later.",
      playerLoseRepeat: "Another win. I'll save the story for camp.",
    },
  },
  "z18-n4": {
    story: "A wall of fire fills an old doorway in front of you.",
    dialogue: {
      intro: "No one gets through. That's what I'm here for. Isn't it?",
      playerWin: "The flames part like an open door. 'Oh! I'm not a wall. I'm a door!'",
      playerLose: "Walls do not open! Go back!",
    },
  },

  // --- Zone 19: Burning Fields (2v2) ---
  "z19-n1": {
    story: "You find Cliff staring at a trail marker. He doesn't recognise it anymore.",
    dialogue: {
      intro: "These markers are important. I know they are. Help me get my head clear. A battle.",
      playerWin: "Sentry's on my shoulder. Now I remember: we keep this path safe. For you.",
      playerLose: "The line held. Now go rest.",
      introRematchAfterLoss: "Back again. Sentry watches me watch the markers.",
      introRematchAfterWin: "Sentry landed on my shoulder. Then I remembered why I watch.",
      playerWinRepeat: "Well fought. The markers lead to the Ash Temple. I remember now.",
      playerLoseRepeat: "Sentry kept us on track. She usually does.",
    },
  },
  "z19-n2": {
    story: "You find Mimi planting shoots in the ash. She names the stubborn one Lulu.",
    dialogue: {
      intro: "Battle me while the plants settle in. Don't step on Lulu. The little one by your foot.",
      playerWin: "No fair! Lulu the plant is a terrible coach.",
      playerLose: "A win! I'm telling both Lulus.",
      introRematchAfterLoss: "Back? I bet double this time!",
      introRematchAfterWin: "Lulu the plant has new leaves. My team has new tricks. Ready?",
      playerWinRepeat: "Fine, you win! I'll tell Lulu I won though.",
      playerLoseRepeat: "Another win! I'm telling the plants until someone cheers.",
    },
  },
  "z19-n3": {
    story: "Vera waits for you with a full pack and news from the temple.",
    dialogue: {
      intro: "Before the temple, a test: get past me without rushing. The spirit inside scares easily.",
      playerWin: "Good. Move slowly when you're inside. It's had enough people charging at it.",
      playerLose: "Too fast. Rest here, then we'll try a calmer approach.",
      introRematchAfterLoss: "You came back. What waits in the temple has not left.",
      introRematchAfterWin: "The hurt spirit is still in the temple. Kneel beside it. Then stay.",
      playerWinRepeat: "That's better. Slow steps when you're inside.",
      playerLoseRepeat: "Still too rushed. Your team needs a rest.",
    },
  },
  "z19-n5": {
    story: "Zip and Dash block the temple path. A bent cage bar hangs from Dash's paw, and Zip waves at you.",
    dialogue: {
      intro: "Zip again! Dash shook off the ash and bent a cage bar doing it! Temple duty is so messy!",
      playerIntro: "Messy? Let's clean this up.",
      playerWin: "Fine! I'm going somewhere I can see my own shoes.",
      playerLose: "Ha! Zip wins! Eat smoke!",
      introRematchAfterLoss: "Back for more? Dash is warmed up!",
      introRematchAfterWin: "Dash, forget the last match. Actually, learn from it. Then forget it!",
      playerWinRepeat: "Fine! Zeck can block you himself!",
      playerLoseRepeat: "Still too slow for Clan Z!",
    },
  },
  "z19-n4": {
    story: "The fires around you try to sing a name. None gets it right.",
    dialogue: {
      intro: "We knew a name. We sang it every night. Listen... No. That's wrong.",
      playerWin: "The voices fall quiet. One whispers at last: \"Wick.\" It points toward the temple. \"We remember that much.\"",
      playerLose: "We sing on! Louder is better out here!",
    },
  },

  // --- Zone 20: Ash Temple (3v3) ---
  "z20-n1": {
    story: "Aggie sits on the temple steps and waves you closer.",
    dialogue: {
      intro: "I know the fire in there, child. It was hurt long ago. Show me you can be gentle.",
      playerWin: "Ready. Sit beside it when you can. Let it decide how close is close enough.",
      playerLose: "Not yet, child. Rest, then come back softer.",
      introRematchAfterLoss: "Back again. Inside burns a spirit that Clan Z caged and hurt.",
      introRematchAfterWin: "They cage what they cannot love, child. You will do the opposite. Kneel beside it.",
      playerWinRepeat: "Well done. The fire settles when someone stays to tend it.",
      playerLoseRepeat: "That spirit has waited years, child. It can wait while you rest.",
    },
  },
  "z20-n5": {
    story: "Zap guards the temple cages. He keeps glancing back at the door, then at you.",
    dialogue: {
      intro: "Shade is the temple door now. No key needed. Zeck says hold it. So we hold it.",
      playerIntro: "Then we'll knock. Politely.",
      playerWin: "Zeck said hold it. I'll tell him we didn't.",
      playerLose: "Quiet wins. The cages stay shut.",
      introRematchAfterLoss: "Back. Shade watched you. We are ready.",
      introRematchAfterWin: "Shade wants another round. The cages stay.",
      playerWinRepeat: "...Fine. Go in. Zeck won't like it.",
      playerLoseRepeat: "The temple stays shut. Clan Z holds.",
    },
  },
  "z20-nw2": {
    story: "A wild pack rises from the temple ash around you.",
    dialogue: {
      intro: "A wild Toro charges through the temple ash! Its pack follows!",
      playerWin: "The wild pack retreats past the temple stones.",
      playerLose: "The wild pack drives you from the temple.",
      introRematchAfterLoss: "The ash stirs. The wild pack returns.",
      introRematchAfterWin: "The pack knows your squad now. Their eyes glow.",
      playerWinRepeat: "The wild pack stamps off into the dark.",
      playerLoseRepeat: "The temple stays wild. The fire burns.",
    },
  },
  "z20-n2": {
    story: "Fire spills toward you from the cage Clan Z left behind.",
    dialogue: {
      intro: "They put me in a cage. I burned my way out. Stay back! My fire keeps everyone away.",
      playerWin: "You're still here. Even after that. Why?",
      playerLose: "See? Everyone goes away. Only fire stays.",
    },
  },
  "z20-n3": {
    story: "Outside the inner room, one last ember glows at your feet.",
    dialogue: {
      intro: "I'm nearly out. A gray man said I could just stop. I won't. Help me find a spark.",
      playerWin: "There it is! Still burning. Go on, Soot the Hotpaw is waiting.",
      playerLose: "Nearly out. Come back before I fade.",
    },
  },
  "z20-n4": {
    story: "Soot the Hotpaw guards the hurt spirit in its broken cage. Zeck watches you from the doorway.",
    dialogue: {
      intro: "Soot flares up when you come close. Calm it down, but do not chase it away from the spirit it guards.",
      playerWin: "You kneel beside the hurt spirit, say \"Wick,\" and stay. Soot calms down and gives you its shard. Inside it glows a picture of the First Keeper! That's fourteen! In the doorway, Zeck lowers his keys.",
      playerLose: "The fire roars you back. Not yet.",
      introRematchAfterLoss: "The fire still flares. In the smoke, Zeck watches and says nothing.",
      introRematchAfterWin: "The fire is small and warm now. Zeck has not forgotten what you did.",
      playerWinRepeat: "Steady and bright. Its shard still shows the First Keeper's face.",
      playerLoseRepeat: "The fire flares once more. Kneel again and stay.",
    },
  },
};
